import { useCallback, useState } from "react";

import { routes } from "~/app/routes/web-routes.ts";
import { useCustomerSelection } from "~/shared/hooks/use-customer-selection.ts";
import type { RoutingGuideType } from "~/shared/schemas/routing-guide-schema.ts";
import type { Customer } from "~/shared/types/quote-details-types.ts";

import { useDeleteRoutingGuide } from "./use-delete-routing-guide.ts";
import { useGetRoutingGuideById } from "./use-get-routing-guide-by-id.ts";

type RoutingGuideLayoutReturn = {
	activeCustomer: Customer | undefined;
	isCustomerLoading: boolean;
	activeGuideId: number | null;
	showRoutingGuideModal: boolean;
	handleOpenEditModal: (guide: RoutingGuideType) => void;
	onClose: () => void;
	handleCustomerChange: (customer: Customer | null) => void;
	handleAddNew: () => void;
	requestDelete: (id: number) => void;
	confirmDelete: () => void;
	cancelDelete: () => void;
	pendingDeleteId: number | null;
	showSimulateModal: boolean;
	handleOpenSimulateModal: () => void;
	handleCloseSimulateModal: () => void;
};

export const useRoutingGuideLayout = (): RoutingGuideLayoutReturn => {
	const { activeCustomer, handleSelectCustomer, isCustomerLoading } =
		useCustomerSelection({
			baseRoute: routes.routingGuidesGlobal,
			customerRoute: routes.routingGuidesWithCustomer,
		});

	const { deleteRoutingGuide } = useDeleteRoutingGuide();
	const { fetchRoutingGuideById } = useGetRoutingGuideById();

	const [showRoutingGuideModal, setShowRoutingGuideModal] = useState(false);
	const [activeGuideId, setActiveGuideId] = useState<number | null>(null);
	const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
	const [showSimulateModal, setShowSimulateModal] = useState(false);

	const handleOpenEditModal = useCallback(
		(guide: RoutingGuideType) => {
			fetchRoutingGuideById(guide.id)
				.then(() => {
					setActiveGuideId(guide.id);
					setShowRoutingGuideModal(true);
				})
				.catch(() => {
					// Error toast is handled inside useGetRoutingGuideById.
				});
		},
		[fetchRoutingGuideById],
	);

	const onClose = useCallback(() => {
		setActiveGuideId(null);
		setShowRoutingGuideModal(false);
	}, []);

	const handleCustomerChange = useCallback(
		(customer: Customer | null) => {
			handleSelectCustomer(customer);
		},
		[handleSelectCustomer],
	);

	const handleAddNew = useCallback(() => {
		setActiveGuideId(Date.now());
		setShowRoutingGuideModal(true);
	}, []);

	const requestDelete = useCallback((id: number): void => {
		setPendingDeleteId(id);
	}, []);

	const confirmDelete = useCallback((): void => {
		if (pendingDeleteId !== null) {
			deleteRoutingGuide(pendingDeleteId);
		}
		setPendingDeleteId(null);
	}, [pendingDeleteId, deleteRoutingGuide]);

	const cancelDelete = useCallback((): void => {
		setPendingDeleteId(null);
	}, []);

	const handleOpenSimulateModal = useCallback(() => {
		setShowSimulateModal(true);
	}, []);

	const handleCloseSimulateModal = useCallback(() => {
		setShowSimulateModal(false);
	}, []);

	return {
		activeCustomer,
		isCustomerLoading,
		activeGuideId,
		showRoutingGuideModal,
		handleOpenEditModal,
		onClose,
		handleCustomerChange,
		handleAddNew,
		requestDelete,
		confirmDelete,
		cancelDelete,
		pendingDeleteId,
		showSimulateModal,
		handleOpenSimulateModal,
		handleCloseSimulateModal,
	};
};
